/**
 * ERSmeta form utilities: required fields validation and JSON building.
 */

import fieldTypeMappingRaw from "../data/fieldTypeMapping.json";
import {
  enrichWithJsonLd,
  type FieldTypeMapping,
  stripJsonLdMeta,
} from "./jsonLdEnricher";

const fieldTypeMapping = fieldTypeMappingRaw as FieldTypeMapping;

const ERSMETA_CONTEXT =
  "https://github.com/NFDI4Energy/ERSmeta/blob/main/schema/ersmeta.jsonld";

export interface RequiredFieldDef {
  key: string;
  tab: string;
  label: string;
}

export const REQUIRED_FIELDS: RequiredFieldDef[] = [
  { key: "name", tab: "General", label: "Software Name" },
  { key: "softwareVersion", tab: "General", label: "Software Version" },
  {
    key: "programmingLanguage",
    tab: "General",
    label: "Programming Languages",
  },
  { key: "description", tab: "General", label: "Description" },
  { key: "keywords", tab: "General", label: "Keywords" },
  { key: "softwareType", tab: "General", label: "Software Type" },
  { key: "metadataVersion", tab: "General", label: "Metadata Version" },
  { key: "dateCreated", tab: "General", label: "Date Created" },
  { key: "developmentStatus", tab: "Development", label: "Development Status" },
  { key: "contributor", tab: "Development", label: "Contributor(s)" },
  { key: "downloadUrl", tab: "Development", label: "Download URL" },
  { key: "license", tab: "Licensing", label: "License" },
  { key: "sdLicense", tab: "Licensing", label: "SD License" },
  { key: "sdPublisher", tab: "Licensing", label: "SD Publisher" },
];

function isEmpty(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}

function isPersonValid(p: Record<string, unknown>): boolean {
  const given = (p.givenName as string)?.trim();
  const family = (p.familyName as string)?.trim();
  return !!(given && family);
}

function isObjectFilled(obj: Record<string, unknown>): boolean {
  return Object.values(obj).some((v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === "string") return v.trim() !== "";
    if (Array.isArray(v))
      return (
        v.length > 0 &&
        v.some((item) =>
          typeof item === "object" && item !== null
            ? isObjectFilled(item as Record<string, unknown>)
            : true,
        )
      );
    if (typeof v === "object")
      return (
        Object.keys(v).length > 0 &&
        isObjectFilled(v as Record<string, unknown>)
      );
    return true;
  });
}

export function getMissingRequiredFields(
  formData: Record<string, unknown>,
): RequiredFieldDef[] {
  const missing: RequiredFieldDef[] = [];

  for (const def of REQUIRED_FIELDS) {
    const rawKey = def.key === "metadataVersion" ? "schemaVersion" : def.key;
    const value = formData[rawKey];

    if (def.key === "contributor") {
      const arr = Array.isArray(value) ? value : [];
      const valid = arr
        .filter(
          (p): p is Record<string, unknown> =>
            typeof p === "object" && p !== null,
        )
        .filter(isPersonValid);
      if (valid.length === 0) missing.push(def);
      continue;
    }

    if (def.key === "license" || def.key === "sdLicense") {
      const arr = Array.isArray(value) ? value : [];
      const filled = arr.filter(
        (o): o is Record<string, unknown> =>
          typeof o === "object" && o !== null && isObjectFilled(o),
      );
      if (filled.length === 0) missing.push(def);
      continue;
    }

    if (isEmpty(value)) missing.push(def);
  }

  return missing;
}

export function allRequiredFieldsFilled(
  formData: Record<string, unknown>,
): boolean {
  return getMissingRequiredFields(formData).length === 0;
}

/** Map form data to ERSmeta JSON. */
export function buildERSmetaJSON(
  formData: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {
    "@context": ERSMETA_CONTEXT,
  };

  const simpleKeys: string[] = [
    "name",
    "acronym",
    "identifier",
    "logo",
    "softwareVersion",
    "abstract",
    "description",
    "graphicalAbstract",
    "developmentStatus",
    "cycleOfUpdates",
    "issueTracker",
    "downloadUrl",
    "codeRepository",
    "readme",
    "buildInstruction",
    "requiredTrainingPeriod",
    "sdPublisher",
    "spatialScope",
    "processorRequirements",
    "memoryRequirements",
    "storageRequirements",
    "fileSize",
    "temporalCoverage",
    "stationaryVsTransient",
  ];

  for (const k of simpleKeys) {
    const v = formData[k];
    if (v !== undefined && v !== null && v !== "")
      out[k] = typeof v === "string" ? v.trim() : v;
  }

  // metadataVersion from schemaVersion
  const schemaVer = formData.schemaVersion as string;
  if (schemaVer?.trim()) out.metadataVersion = schemaVer.trim();

  // dateCreated, dateModified
  const dc = formData.dateCreated as string;
  if (dc?.trim()) out.dateCreated = dc.trim();
  const dm = formData.dateModified as string;
  if (dm?.trim()) out.dateModified = dm.trim();

  // website -> url (array)
  const website = formData.website as string[] | undefined;
  if (Array.isArray(website) && website.length > 0) {
    const urls = website.filter((s) => typeof s === "string" && s.trim());
    if (urls.length > 0) out.url = urls;
  }

  // String arrays
  const stringArrayKeys = [
    "programmingLanguage",
    "inLanguage",
    "keywords",
    "documentation",
    "purpose",
    "method",
    "useCase",
    "developerStructure",
    "domain",
    "category",
    "roleInResearch",
    "assumption",
    "limitation",
    "typicalHardware",
    "realtimeCapability",
    "operatingSystem",
    "targetProduct",
    "softwareRequirements",
    "featureList",
    "parameter",
    "compatibleHardware",
    "coversSector",
    "technology",
    "hasTemporalResolution",
    "hasSpatialResolution",
    "dimensions",
    "spatialCoverage",
    "supportedVoltageLevel",
  ];
  for (const k of stringArrayKeys) {
    const v = formData[k];
    if (Array.isArray(v) && v.length > 0) {
      const arr = v
        .filter((x) => typeof x === "string" && (x as string).trim())
        .map((x) => (x as string).trim());
      if (arr.length > 0) out[k] = arr;
    }
  }

  // softwareType
  const st = formData.softwareType;
  if (Array.isArray(st) && st.length > 0) out.softwareType = st;

  // Boolean
  const supportGiven = formData.supportGiven;
  if (supportGiven === true || supportGiven === false)
    out.supportGiven = supportGiven;
  const isFree = formData.isAccessibleForFree;
  if (isFree === true || isFree === false) out.isAccessibleForFree = isFree;
  const parallel = formData.parallelizationCapability;
  if (parallel === true || parallel === false)
    out.parallelizationCapability = parallel;

  // Number array
  const typicalExec = formData.typicalExecutionTime;
  if (Array.isArray(typicalExec) && typicalExec.length > 0) {
    const nums = typicalExec.filter(
      (x) => typeof x === "number" && !Number.isNaN(x),
    );
    if (nums.length > 0) out.typicalExecutionTime = nums;
  }

  // countryOfOrigin
  const co = formData.countryOfOrigin;
  if (Array.isArray(co) && co.length > 0) out.countryOfOrigin = co;

  // Person arrays: contributor, author, maintainer
  const personKeys = ["contributor", "author", "maintainer"] as const;
  for (const pk of personKeys) {
    const arr = formData[pk] as Array<Record<string, unknown>> | undefined;
    if (Array.isArray(arr) && arr.length > 0) {
      const persons = arr
        .map((p) => {
          if (!p || typeof p !== "object") return null;
          const given = (p.givenName as string)?.trim();
          const family = (p.familyName as string)?.trim();
          if (!given && !family) return null;
          const obj: Record<string, unknown> = {};
          if (given) obj.givenName = given;
          if (family) obj.familyName = family;
          if ((p.identifier as string)?.trim())
            obj.identifier = (p.identifier as string).trim();
          if ((p.email as string[])?.length)
            obj.email = (p.email as string[]).filter(
              (s) => typeof s === "string" && s.trim(),
            );
          if ((p.roleName as string[])?.length)
            obj.roleName = (p.roleName as string[]).filter(
              (s) => typeof s === "string" && s.trim(),
            );
          return Object.keys(obj).length > 0 ? obj : null;
        })
        .filter(
          (x): x is Record<string, unknown> =>
            x !== null && !!(x.givenName || x.familyName),
        );
      if (persons.length > 0) out[pk] = persons;
    }
  }

  // Organization arrays
  const orgKeys = ["sourceOrganization", "copyrightHolder"] as const;
  for (const ok of orgKeys) {
    const arr = formData[ok] as Array<Record<string, unknown>> | undefined;
    if (Array.isArray(arr) && arr.length > 0) {
      const orgs = arr
        .map((o) => {
          if (!o || typeof o !== "object") return null;
          const legal = (o.legalName as string)?.trim();
          const id = (o.identifier as string)?.trim();
          if (!legal && !id) return null;
          const obj: Record<string, unknown> = {};
          if (legal) obj.legalName = legal;
          if (id) obj.identifier = id;
          return Object.keys(obj).length > 0 ? obj : null;
        })
        .filter((x): x is Record<string, unknown> => x !== null);
      if (orgs.length > 0) out[ok] = orgs;
    }
  }

  // CreativeWork arrays: license, sdLicense
  const cwKeys = ["license", "sdLicense"] as const;
  for (const ck of cwKeys) {
    const arr = formData[ck] as Array<Record<string, unknown>> | undefined;
    if (Array.isArray(arr) && arr.length > 0) {
      const items = arr
        .map((o) => {
          if (!o || typeof o !== "object") return null;
          const id = (o.identifier as string)?.trim();
          const name = (o.name as string)?.trim();
          if (!id && !name) return null;
          const obj: Record<string, unknown> = {};
          if (id) obj.identifier = id;
          if (name) obj.name = name;
          return Object.keys(obj).length > 0 ? obj : null;
        })
        .filter((x): x is Record<string, unknown> => x !== null);
      if (items.length > 0) out[ck] = items;
    }
  }

  // MonetaryAmount: estimatedCost
  const est = formData.estimatedCost as Record<string, unknown> | undefined;
  if (est && typeof est === "object") {
    const curr = (est.currency as string)?.trim();
    const val = (est.value as string)?.trim();
    if (curr || val)
      out.estimatedCost = {
        ...(curr && { currency: curr }),
        ...(val && { value: val }),
      };
  }

  // Funding (Grant array): projectName -> name, grantNo -> identifier
  const funding = formData.funding as
    | Array<Record<string, unknown>>
    | undefined;
  if (Array.isArray(funding) && funding.length > 0) {
    const grants = funding
      .map((g) => {
        if (!g || typeof g !== "object") return null;
        const projectName = (g.projectName as string)?.trim();
        const grantNo = (g.grantNo as string)?.trim();
        const funders = g.funder as Array<Record<string, unknown>> | undefined;
        const funderOrgs = Array.isArray(funders)
          ? funders
              .map((f) => {
                if (!f || typeof f !== "object") return null;
                const ln = (f.legalName as string)?.trim();
                const id = (f.identifier as string)?.trim();
                if (!ln && !id) return null;
                return {
                  ...(ln && { legalName: ln }),
                  ...(id && { identifier: id }),
                };
              })
              .filter((x): x is Record<string, unknown> => x !== null)
          : [];
        if (!projectName && !grantNo && funderOrgs.length === 0) return null;
        const obj: Record<string, unknown> = {};
        if (projectName) obj.name = projectName;
        if (grantNo) obj.identifier = grantNo;
        if (funderOrgs.length > 0) obj.funder = funderOrgs;
        return Object.keys(obj).length > 0 ? obj : null;
      })
      .filter((x): x is Record<string, unknown> => x !== null);
    if (grants.length > 0) out.funding = grants;
  }

  // ScholarlyArticle arrays and single
  const refPub = formData.referencePublication as
    | Array<Record<string, unknown>>
    | undefined;
  if (Array.isArray(refPub) && refPub.length > 0) {
    const items = refPub
      .map((a) =>
        (a?.identifier as string)?.trim()
          ? { identifier: (a.identifier as string).trim() }
          : null,
      )
      .filter((x): x is { identifier: string } => x !== null);
    if (items.length > 0) out.referencePublication = items;
  }

  const prefCite = formData.preferredCitation as
    | Record<string, unknown>
    | undefined;
  if (
    prefCite &&
    typeof prefCite === "object" &&
    (prefCite.identifier as string)?.trim()
  ) {
    out.preferredCitation = {
      identifier: (prefCite.identifier as string).trim(),
    };
  }

  const usedPub = formData.usedInPublication as
    | Array<Record<string, unknown>>
    | undefined;
  if (Array.isArray(usedPub) && usedPub.length > 0) {
    const items = usedPub
      .map((a) =>
        (a?.identifier as string)?.trim()
          ? { identifier: (a.identifier as string).trim() }
          : null,
      )
      .filter((x): x is { identifier: string } => x !== null);
    if (items.length > 0) out.usedInPublication = items;
  }

  // OptimizationTool array
  const opt = formData.usedOptimization as
    | Array<Record<string, unknown>>
    | undefined;
  if (Array.isArray(opt) && opt.length > 0) {
    const items = opt
      .map((o) => {
        if (!o || typeof o !== "object") return null;
        const obj: Record<string, unknown> = {};
        const fl = o.featureList;
        if (Array.isArray(fl) && fl.length > 0)
          obj.featureList = fl.filter(
            (s: unknown) => typeof s === "string" && (s as string).trim(),
          );
        const meth = o.method;
        if (Array.isArray(meth) && meth.length > 0)
          obj.method = meth.filter(
            (s: unknown) => typeof s === "string" && (s as string).trim(),
          );
        if ((o.problemType as string)?.trim())
          obj.problemType = (o.problemType as string).trim();
        const solv = o.solver;
        if (Array.isArray(solv) && solv.length > 0)
          obj.solver = solv.filter(
            (s: unknown) => typeof s === "string" && (s as string).trim(),
          );
        return Object.keys(obj).length > 0 ? obj : null;
      })
      .filter((x): x is Record<string, unknown> => x !== null);
    if (items.length > 0) out.usedOptimization = items;
  }

  // Interface arrays: input, output
  const ifKeys = ["input", "output"] as const;
  for (const ik of ifKeys) {
    const arr = formData[ik] as Array<Record<string, unknown>> | undefined;
    if (Array.isArray(arr) && arr.length > 0) {
      const items = arr
        .map((i) => {
          if (!i || typeof i !== "object") return null;
          const obj: Record<string, unknown> = {};
          if ((i.identifier as string)?.trim())
            obj.identifier = (i.identifier as string).trim();
          const us = i.usedStandard;
          if (Array.isArray(us) && us.length > 0)
            obj.usedStandard = us.filter(
              (s: unknown) => typeof s === "string" && (s as string).trim(),
            );
          const ex = i.example;
          if (Array.isArray(ex) && ex.length > 0)
            obj.example = ex.filter(
              (s: unknown) => typeof s === "string" && (s as string).trim(),
            );
          if ((i.description as string)?.trim())
            obj.description = (i.description as string).trim();
          const ef = i.encodingFormat;
          if (Array.isArray(ef) && ef.length > 0) obj.encodingFormat = ef;
          const htr = i.hasTemporalResolution;
          if (Array.isArray(htr) && htr.length > 0)
            obj.hasTemporalResolution = htr;
          if (i.required !== undefined) obj.required = i.required;
          return Object.keys(obj).length > 0 ? obj : null;
        })
        .filter((x): x is Record<string, unknown> => x !== null);
      if (items.length > 0) out[ik] = items;
    }
  }

  // SoftwareApplication, DataSet, EnergyComponent, CommunityInteraction, Review, TestCollection, Validation, Example
  const compatibleSoft = formData.compatibleSoftware as
    | Array<Record<string, unknown>>
    | undefined;
  if (Array.isArray(compatibleSoft) && compatibleSoft.length > 0) {
    const items = compatibleSoft
      .map((s) => {
        if (!s || typeof s !== "object") return null;
        const obj: Record<string, unknown> = {};
        if ((s.identifier as string)?.trim())
          obj.identifier = (s.identifier as string).trim();
        if ((s.name as string)?.trim()) obj.name = (s.name as string).trim();
        const surl = s.url;
        if (Array.isArray(surl) && surl.length > 0)
          obj.url = surl.filter(
            (x: unknown) => typeof x === "string" && (x as string).trim(),
          );
        return Object.keys(obj).length > 0 ? obj : null;
      })
      .filter((x): x is Record<string, unknown> => x !== null);
    if (items.length > 0) out.compatibleSoftware = items;
  }

  const dataKeys = ["compatibleData", "usedData"] as const;
  for (const dk of dataKeys) {
    const arr = formData[dk] as Array<Record<string, unknown>> | undefined;
    if (Array.isArray(arr) && arr.length > 0) {
      const items = arr
        .map((d) => {
          if (!d || typeof d !== "object") return null;
          const obj: Record<string, unknown> = {};
          if ((d.identifier as string)?.trim())
            obj.identifier = (d.identifier as string).trim();
          const def = d.encodingFormat;
          if (Array.isArray(def) && def.length > 0) obj.encodingFormat = def;
          const durl = d.url;
          if (Array.isArray(durl) && durl.length > 0)
            obj.url = durl.filter(
              (x: unknown) => typeof x === "string" && (x as string).trim(),
            );
          return Object.keys(obj).length > 0 ? obj : null;
        })
        .filter((x): x is Record<string, unknown> => x !== null);
      if (items.length > 0) out[dk] = items;
    }
  }

  const energyComp = formData.energyComponent as
    | Array<Record<string, unknown>>
    | undefined;
  if (Array.isArray(energyComp) && energyComp.length > 0) {
    const items = energyComp
      .map((e) => {
        if (!e || typeof e !== "object") return null;
        const obj: Record<string, unknown> = {};
        if ((e.identifier as string)?.trim())
          obj.identifier = (e.identifier as string).trim();
        if ((e.additionalType as string)?.trim())
          obj.additionalType = (e.additionalType as string).trim();
        if ((e.name as string)?.trim()) obj.name = (e.name as string).trim();
        if ((e.levelOfDetail as string)?.trim())
          obj.levelOfDetail = (e.levelOfDetail as string).trim();
        return Object.keys(obj).length > 0 ? obj : null;
      })
      .filter((x): x is Record<string, unknown> => x !== null);
    if (items.length > 0) out.energyComponent = items;
  }

  const commInt = formData.communityInteractions as
    | Array<Record<string, unknown>>
    | undefined;
  if (Array.isArray(commInt) && commInt.length > 0) {
    const items = commInt
      .map((c) => {
        if (!c || typeof c !== "object") return null;
        const obj: Record<string, unknown> = {};
        if ((c.additionalType as string)?.trim())
          obj.additionalType = (c.additionalType as string).trim();
        if ((c.url as string)?.trim()) obj.url = (c.url as string).trim();
        return Object.keys(obj).length > 0 ? obj : null;
      })
      .filter((x): x is Record<string, unknown> => x !== null);
    if (items.length > 0) out.communityInteractions = items;
  }

  const review = formData.review as Array<Record<string, unknown>> | undefined;
  if (Array.isArray(review) && review.length > 0) {
    const items = review
      .map((r) => {
        if (!r || typeof r !== "object") return null;
        const obj: Record<string, unknown> = {};
        if ((r.reviewAspect as string)?.trim())
          obj.reviewAspect = (r.reviewAspect as string).trim();
        if ((r.reviewBody as string)?.trim())
          obj.reviewBody = (r.reviewBody as string).trim();
        if ((r.abstract as string)?.trim())
          obj.abstract = (r.abstract as string).trim();
        return Object.keys(obj).length > 0 ? obj : null;
      })
      .filter((x): x is Record<string, unknown> => x !== null);
    if (items.length > 0) out.review = items;
  }

  const tests = formData.tests as Array<Record<string, unknown>> | undefined;
  if (Array.isArray(tests) && tests.length > 0) {
    const items = tests
      .map((t) => {
        if (!t || typeof t !== "object") return null;
        const obj: Record<string, unknown> = {};
        if ((t.collectionSize as string)?.trim())
          obj.collectionSize = (t.collectionSize as string).trim();
        const turl = t.url;
        if (Array.isArray(turl) && turl.length > 0)
          obj.url = turl.filter(
            (x: unknown) => typeof x === "string" && (x as string).trim(),
          );
        return Object.keys(obj).length > 0 ? obj : null;
      })
      .filter((x): x is Record<string, unknown> => x !== null);
    if (items.length > 0) out.tests = items;
  }

  const validation = formData.validation as
    | Array<Record<string, unknown>>
    | undefined;
  if (Array.isArray(validation) && validation.length > 0) {
    const items = validation
      .map((v) => {
        if (!v || typeof v !== "object") return null;
        const obj: Record<string, unknown> = {};
        if ((v.additionalType as string)?.trim())
          obj.additionalType = (v.additionalType as string).trim();
        if ((v.similarityOfResults as string)?.trim())
          obj.similarityOfResults = (v.similarityOfResults as string).trim();
        if ((v.url as string)?.trim()) obj.url = (v.url as string).trim();
        return Object.keys(obj).length > 0 ? obj : null;
      })
      .filter((x): x is Record<string, unknown> => x !== null);
    if (items.length > 0) out.validation = items;
  }

  const example = formData.example as
    | Array<Record<string, unknown>>
    | undefined;
  if (Array.isArray(example) && example.length > 0) {
    const items = example
      .map((e) => {
        if (!e || typeof e !== "object") return null;
        const obj: Record<string, unknown> = {};
        if ((e.additionalType as string)?.trim())
          obj.additionalType = (e.additionalType as string).trim();
        if ((e.name as string)?.trim()) obj.name = (e.name as string).trim();
        if ((e.url as string)?.trim()) obj.url = (e.url as string).trim();
        return Object.keys(obj).length > 0 ? obj : null;
      })
      .filter((x): x is Record<string, unknown> => x !== null);
    if (items.length > 0) out.example = items;
  }

  return out;
}

/** Build a valid ERSmeta JSON-LD document from form data.
 *  Composes: plain JSON → recursive enrichment via mapping → root @type. */
export function buildERSmetaJsonLd(
  formData: Record<string, unknown>,
): Record<string, unknown> {
  const json = buildERSmetaJSON(formData);
  const enriched = enrichWithJsonLd(json, fieldTypeMapping);

  const { "@context": context, ...rest } = enriched;
  const result: Record<string, unknown> = {
    "@context": context,
    "@type": "EnergyResearchSoftware",
  };

  if (typeof rest.identifier === "string" && rest.identifier) {
    result["@id"] = rest.identifier;
  }

  Object.assign(result, rest);
  return result;
}

/** Parse ERSmeta JSON/JSON-LD into form data (reverse of buildERSmetaJSON).
 *  Strips all JSON-LD metadata (@context, @type, @id) before parsing. */
export function parseERSmetaToFormData(
  rawJson: Record<string, unknown>,
): Record<string, unknown> {
  const json = stripJsonLdMeta(rawJson) as Record<string, unknown>;
  const out: Record<string, unknown> = { schemaVersion: "ERSmeta-V0.9" };

  const simpleKeys: string[] = [
    "name",
    "acronym",
    "identifier",
    "logo",
    "softwareVersion",
    "abstract",
    "description",
    "graphicalAbstract",
    "developmentStatus",
    "cycleOfUpdates",
    "issueTracker",
    "downloadUrl",
    "codeRepository",
    "readme",
    "buildInstruction",
    "requiredTrainingPeriod",
    "sdPublisher",
    "spatialScope",
    "processorRequirements",
    "memoryRequirements",
    "storageRequirements",
    "fileSize",
    "temporalCoverage",
    "stationaryVsTransient",
  ];

  for (const k of simpleKeys) {
    const v = json[k];
    if (v !== undefined && v !== null && v !== "")
      out[k] = typeof v === "string" ? v.trim() : v;
  }

  // metadataVersion -> schemaVersion
  const metaVer = json.metadataVersion as string;
  if (metaVer?.trim()) out.schemaVersion = metaVer.trim();

  // dateCreated, dateModified
  const dc = json.dateCreated as string;
  if (dc?.trim()) out.dateCreated = dc.trim();
  const dm = json.dateModified as string;
  if (dm?.trim()) out.dateModified = dm.trim();

  // url -> website (array)
  const urlVal = json.url;
  if (Array.isArray(urlVal) && urlVal.length > 0) {
    out.website = urlVal.filter(
      (s): s is string => typeof s === "string" && !!s.trim(),
    );
  } else if (typeof urlVal === "string" && urlVal.trim()) {
    out.website = [urlVal.trim()];
  }

  // String arrays
  const stringArrayKeys = [
    "programmingLanguage",
    "inLanguage",
    "keywords",
    "documentation",
    "purpose",
    "method",
    "useCase",
    "developerStructure",
    "domain",
    "category",
    "roleInResearch",
    "assumption",
    "limitation",
    "typicalHardware",
    "realtimeCapability",
    "operatingSystem",
    "targetProduct",
    "softwareRequirements",
    "featureList",
    "parameter",
    "compatibleHardware",
    "coversSector",
    "technology",
    "hasTemporalResolution",
    "hasSpatialResolution",
    "dimensions",
    "spatialCoverage",
    "supportedVoltageLevel",
  ];
  for (const k of stringArrayKeys) {
    const v = json[k];
    if (Array.isArray(v) && v.length > 0) {
      const arr = v
        .filter(
          (x): x is string => typeof x === "string" && !!(x as string).trim(),
        )
        .map((x) => (x as string).trim());
      if (arr.length > 0) out[k] = arr;
    }
  }

  const st = json.softwareType;
  if (Array.isArray(st) && st.length > 0) out.softwareType = st;

  // Booleans
  if (json.supportGiven === true || json.supportGiven === false)
    out.supportGiven = json.supportGiven;
  if (json.isAccessibleForFree === true || json.isAccessibleForFree === false)
    out.isAccessibleForFree = json.isAccessibleForFree;
  if (
    json.parallelizationCapability === true ||
    json.parallelizationCapability === false
  )
    out.parallelizationCapability = json.parallelizationCapability;

  const typicalExec = json.typicalExecutionTime;
  if (Array.isArray(typicalExec) && typicalExec.length > 0)
    out.typicalExecutionTime = typicalExec.filter((x) => typeof x === "number");

  const co = json.countryOfOrigin;
  if (Array.isArray(co) && co.length > 0) out.countryOfOrigin = co;

  // Person arrays (same structure)
  const personKeys = ["contributor", "author", "maintainer"] as const;
  for (const pk of personKeys) {
    const arr = json[pk] as Array<Record<string, unknown>> | undefined;
    if (Array.isArray(arr) && arr.length > 0) out[pk] = arr;
  }

  // Organization arrays (same structure)
  const orgKeys = ["sourceOrganization", "copyrightHolder"] as const;
  for (const ok of orgKeys) {
    const arr = json[ok] as Array<Record<string, unknown>> | undefined;
    if (Array.isArray(arr) && arr.length > 0) out[ok] = arr;
  }

  // CreativeWork arrays (same structure)
  const cwKeys = ["license", "sdLicense"] as const;
  for (const ck of cwKeys) {
    const arr = json[ck] as Array<Record<string, unknown>> | undefined;
    if (Array.isArray(arr) && arr.length > 0) out[ck] = arr;
  }

  // MonetaryAmount
  const est = json.estimatedCost as Record<string, unknown> | undefined;
  if (est && typeof est === "object") out.estimatedCost = est;

  // Funding: name -> projectName, identifier -> grantNo (support both schema and check.json formats)
  const funding = json.funding as Array<Record<string, unknown>> | undefined;
  if (Array.isArray(funding) && funding.length > 0) {
    out.funding = funding.map((g) => {
      const name = (g.name ?? g.projectName) as string | undefined;
      const id = (g.identifier ?? g.grantNo) as string | undefined;
      const funders = g.funder as Array<Record<string, unknown>> | undefined;
      return {
        ...(name?.trim() && { projectName: name.trim() }),
        ...(id?.trim() && { grantNo: id.trim() }),
        ...(Array.isArray(funders) &&
          funders.length > 0 && { funder: funders }),
      };
    });
  }

  // ScholarlyArticle
  const refPub = json.referencePublication as
    | Array<Record<string, unknown>>
    | undefined;
  if (Array.isArray(refPub) && refPub.length > 0)
    out.referencePublication = refPub;

  const prefCite = json.preferredCitation as
    | Record<string, unknown>
    | undefined;
  if (prefCite && typeof prefCite === "object")
    out.preferredCitation = prefCite;

  const usedPub = json.usedInPublication as
    | Array<Record<string, unknown>>
    | undefined;
  if (Array.isArray(usedPub) && usedPub.length > 0)
    out.usedInPublication = usedPub;

  // Rest: same structure
  const sameStructureKeys = [
    "usedOptimization",
    "input",
    "output",
    "compatibleSoftware",
    "compatibleData",
    "usedData",
    "energyComponent",
    "communityInteractions",
    "review",
    "tests",
    "validation",
    "example",
  ] as const;
  for (const k of sameStructureKeys) {
    const v = json[k];
    if (v !== undefined && v !== null) out[k] = v;
  }

  return out;
}
